import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Translate, I18n } from "react-redux-i18n";
import { Label, Dropdown, TextField } from "cakemail-ui-components/lib/react";
import PropTypes from "prop-types";
import BoxIcon from "assets/js/scenes/brand/components/BoxIcon";
import {
	getBestImageFormat,
	getSDKImgObj,
	getSDKImgObjFormat,
} from "assets/js/scenes/brand/utils";
import { updateLocalBrand } from "assets/js/scenes/brand/actions";
import { getProfileLogoUrl, isValidUrl } from "utils/urlUtils";
import { loadImage } from "utils/dataUtils";
import { TDefaultImgObj, TImgObj } from "../../PropTypes";

export const BrandImageSelection = (props) => {
	const [imgObj, setImgObj] = useState(props.imgObj);
	const [sdkImg, setSDKImg] = useState(null);
	const [sdkImgFormat, setSDKImgFormat] = useState(null);
	const [addProfileLogo, setAddProfileLogo] = useState(null);
	const preSelectedPrimary = useRef(null);
	const profileLogoUrl = getProfileLogoUrl(props.accountId);
	const usingDirectUrl = isValidUrl(imgObj?.id);

	useEffect(() => {
		loadImage(profileLogoUrl).then(() => {
			setAddProfileLogo(true);
		});
	}, []);

	useEffect(() => {
		setSDKStates();
	}, [props.userBrandSDK]);

	useEffect(() => {
		if (preSelectedPrimary.current) {
			handleSDKFieldChange(null, null, {
				...preSelectedPrimary.current,
				skipUnpublisedDelete: true,
			});
		}
	}, [preSelectedPrimary.current]);

	function setSDKStates(imgObjRef = null) {
		imgObjRef = imgObjRef ? imgObjRef : imgObj;
		if (imgObjRef?.id) {
			if (isValidUrl(imgObjRef.id)) {
				setSDKImgFormat({ src: imgObjRef.id });
			} else {
				const sdkImgObject = getSDKImgObj(imgObjRef?.id);
				setSDKImg(sdkImgObject);

				if (imgObjRef?.type && sdkImgObject) {
					setSDKImgFormat(
						getSDKImgObjFormat(sdkImgObject.formats, imgObjRef.type)
					);
				}
			}
		}
	}

	function updateLocalBrand(value, skipUnpublisedDelete = false) {
		if (!props.path) {
			console.warn("Brand image selection has no path");
		} else {
			if (
				skipUnpublisedDelete === false &&
				JSON.stringify(value) === JSON.stringify(props.defaultImgObj)
			) {
				props.updateLocalBrand(
					props.path,
					value,
					{ value: value, valid: "valid" },
					true
				);
			} else {
				props.updateLocalBrand(props.path, value, {
					value: value,
					valid: "valid",
				});
			}
		}
	}

	function handleSDKFieldChange(e, input, option) {
		const isValidURl = isValidUrl(option.value);
		const newObj = { ...imgObj };

		newObj.id = option.value;

		if (isValidURl) {
			newObj.type = "default";
			setSDKImg(null);
			setSDKImgFormat({ src: newObj.id });
		} else {
			newObj.type = getBestImageFormat(
				getSDKImgObj(option.value).formats
			).format;
			setSDKStates(newObj);
		}

		setImgObj(newObj);
		updateLocalBrand(newObj, option.skipUnpublisedDelete);
	}

	function handleWidthChange(e) {
		const newObj = {
			...imgObj,
			width: parseInt(e.currentTarget.value),
		};
		setImgObj(newObj);
		updateLocalBrand(newObj);
	}

	let imgOptions = [];
	let formatTypeOptions = [];
	let hasASelection = false;

	if (props.userBrandSDK) {
		imgOptions =
			props.userBrandSDK?.logos?.map((logo) => {
				const bestFormat = getBestImageFormat(logo.formats);
				const selected = props?.imgObj
					? logo.id === props?.imgObj?.id
					: logo.tag === "Primary";

				if (
					props.usePrimary &&
					!props?.imgObj?.id &&
					logo.tag === "Primary" &&
					!preSelectedPrimary.current
				) {
					preSelectedPrimary.current = { img: bestFormat?.src, value: logo.id };
				}

				if (!hasASelection && selected) {
					hasASelection = selected;
				}

				return {
					label: (
						<>
							<span>{logo.name}</span> {logo.tag && <Label text={logo.tag} />}
						</>
					),
					value: logo.id,
					icon: <BoxIcon type="img" url={bestFormat?.src} />,
					img: bestFormat?.src,
					selected: selected,
				};
			}) || [];

		if (sdkImg) {
			formatTypeOptions = sdkImg.formats.map((format) => {
				return {
					value: format.format,
					label: format.format,
					selected: format.format === imgObj.type,
				};
			});

			formatTypeOptions.push({
				label: <Translate value="app-brand-logo-type-field-add-new" />,
				onClick: () => {
					props.goToSDK("");
				},
			});
		}
	}

	if (addProfileLogo) {
		const urlWithoutBust = profileLogoUrl.substring(
			0,
			profileLogoUrl.indexOf("?bust")
		);
		imgOptions.push({
			label: (
				<Translate
					value="app-brand-element-image-selection-default-profile"
					resellerName={props.resellerName}
				/>
			),
			value: profileLogoUrl,
			icon: <BoxIcon type="img" url={profileLogoUrl} />,
			img: profileLogoUrl,
			selected: props?.imgObj?.id?.includes(urlWithoutBust) || !hasASelection,
		});
	}

	imgOptions.push({
		className: "addNew",
		label: <Translate value="app-brand-logo-field-add-new" />,
		onClick: () => {
			props.goToSDK("");
		},
	});

	return (
		<div className="brandElement-image-selection">
			{sdkImgFormat ? (
				<div className="imgPreviewContainer">
					<img src={sdkImgFormat.src} />
				</div>
			) : (
				<>
					{!hasASelection && addProfileLogo && (
						<div className="imgPreviewContainer">
							<img src={profileLogoUrl} />
						</div>
					)}
				</>
			)}

			<Dropdown
				key={`${props.userBrandSDK ? "loadedSDK" : ""}-${
					addProfileLogo ? "addedProfileLogo" : ""
				}`}
				className={`imgDropdown ${
					sdkImgFormat || (!hasASelection && addProfileLogo)
						? "imgAvailable"
						: ""
				}`}
				type="custom"
				shapeSytle="outline"
				options={imgOptions}
				placeholder={I18n.t("app-brand-element-image-selection-placeholder")}
				onChange={handleSDKFieldChange}
			/>
			{imgObj?.id && imgObj?.id !== "" && (
				<>
					{!usingDirectUrl && (
						<Dropdown
							key={sdkImg ? `loaded-sdkimg-${imgObj.id}` : ""}
							type="custom"
							shapeSytle="outline"
							options={formatTypeOptions}
							placeholder={I18n.t(
								"app-brand-element-image-selection-type-placeholder"
							)}
							onChange={handleSDKFieldChange}
						/>
					)}
					<TextField
						placeholder={I18n.t(
							"app-brand-element-image-selection-width-placeholder"
						)}
						value={imgObj.width || ""}
						onChange={handleWidthChange}
						type="number"
					/>
				</>
			)}
		</div>
	);
};

BrandImageSelection.propTypes = {
	path: PropTypes.string,
	defaultImgObj: PropTypes.shape(TDefaultImgObj),
	updateLocalBrand: PropTypes.func,
	userBrandSDK: PropTypes.object,
	imgObj: PropTypes.shape(TImgObj),
	goToSDK: PropTypes.func,
	usePrimary: PropTypes.bool,
	accountId: PropTypes.string,
	resellerName: PropTypes.string,
};

BrandImageSelection.defaultProps = {
	defaultImgObj: {
		id: "",
		url: null,
		width: null,
		type: "",
	},
	imgObj: null,
	goToSDK: () => {},
	usePrimary: false,
	resellerName: "",
};

export default withRouter(
	connect(
		(state) => {
			return {
				userBrandSDK: state.userBrandSDK,
				accountId: state.global.account?.id,
				resellerName: state.brand?.name,
			};
		},
		(dispatch) =>
			bindActionCreators(
				{
					updateLocalBrand,
				},
				dispatch
			)
	)(BrandImageSelection)
);
